import { combineReducers } from 'redux';
import axios from 'axios';
import { getFormValues, autofill, stopSubmit, startSubmit } from 'redux-form';
import i18next from 'i18next';

import { SAVE_THEN_MOVE_TO_EDIT } from 'src/constants/Generals';
import { EMPLOYEE_FORM } from 'src/constants/FormNames';
import * as Urls from 'src/constants/EndpointUrls';
import { serializeHttpGetParams, concatParamsToUrl, redirectTo } from 'src/utils/Http';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { newItem, newItemFailure, editItem, editItemFailure, getItem } from 'src/reducers/common/itemReducer';
import { getListQueries, fetchList, fetchListSuccess, fetchListFailure, setExtras, setQueries } from 'src/reducers/common/listReducer';
import selector, { isIncluded, scrollToTop } from 'src/utils/Utils';
import { isHonnin, paramsWithFile } from 'src/utils/Form';
import { REDUCER_NAME as MEMO_REDUCER_NAME } from 'src/reducers/memos/memos';
import { setGlobalSuccesses, setGlobalErrors } from 'src/reducers/global';
import { getBase64 } from '../../utils/Reducer';
import { getTutorialStep3, setTutorialStep3 } from '../../utils/Tutorial';
import { fetchItemFailure, mergeItemFields, setLoading } from '../common/itemReducer';
import { removeZeroYenItem } from '../../features/MyData/utils';

export const REDUCER_NAME = 'employees';

export const getEmployeeData = state => getItem(REDUCER_NAME, state);

// Form Selector
export const getEmployeeFormValues = state => getFormValues(EMPLOYEE_FORM)(state);

// actions
export const updateEmployeeForm = (field, value) => autofill(EMPLOYEE_FORM, field, value);

// Async Action Creators
export const fetchEmployees = (queries, callbackWithDispatch = undefined) => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    const response = await axios.get(`${Urls.SEARCH_EMPLOYEE_URL}`, { params: queries });
    const { totalCount = 0, displayFrom = 0, displayTo = 0 } = response.data.payload;
    const payload = {
      data: response.data.payload.employees,
      pageCount: response.data.payload.totalPages,
      totalCount,
      displayFrom,
      displayTo
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(MEMO_REDUCER_NAME, response.data.payload.memos));
    if (typeof callbackWithDispatch === 'function') {
      dispatch(callbackWithDispatch(payload.data));
    }
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const fetchEmployeeWithExtraCondition = data => async (dispatch, getState) => {
  try {
    // Get parameter from form data
    const { nameCnt, groupIds, employmentTypeIds, occupationIds, payrollRulesGroup, positionIds, noMemo } = data;
    let { memoColorIds } = data;
    if (memoColorIds) {
      memoColorIds = Object.keys(memoColorIds)
        .map(key => (memoColorIds[key] ? key : null))
        .toString();
    }
    // Current query
    const originQueries = getListQueries(REDUCER_NAME, getState());
    // 1ページ目から検索
    const newQueries = {
      ...originQueries,
      nameCnt,
      groupIds,
      employmentTypeIds,
      occupationIds,
      payrollRulesGroup,
      positionIds,
      noMemo,
      memoColorIds,
      page: 1
    };
    dispatch(setQueries(REDUCER_NAME, newQueries));
    // Re fetch employee bonus
    dispatch(fetchEmployees());
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

// New Employee
export const newEmployee = data => async dispatch => {
  try {
    dispatch(newItem(REDUCER_NAME));
    const httpBody = { employee: data, employeePayrollRule: data };
    const response = await axios.post(Urls.CREATE_EMPLOYEE_URL, httpBody);

    if (response.data.errors.length > 0) {
      dispatch(editItemFailure(REDUCER_NAME, response.data.errors.messages));
    } else {
      const values = response.data.payload;
      // It redirects immediately after success, so we do not need dispatch here.
      // Uncomment below line in otherwise.
      // dispatch(newSuccessAction(REDUCER_NAME, values.employee));
      const nextUrl = data.afterSubmit === SAVE_THEN_MOVE_TO_EDIT ? concatParamsToUrl(Urls.EMPLOYEE_EDIT, { id: values.employee.id }) : Urls.EMPLOYEES_LIST;
      const tutorialStep3 = getTutorialStep3();
      if (tutorialStep3) {
        const { run, employeeId } = tutorialStep3;
        if (run) {
          setTutorialStep3({ run, stepIndex: 0, tourCompleted: false, type: 'checkInfo', employeeId });
        } else {
          setTutorialStep3({ run, stepIndex: 0, tourCompleted: true, type: 'checkInfo', employeeId });
        }
      }
      redirectTo(nextUrl);
    }
  } catch (exception) {
    scrollToTop();
    dispatch(stopSubmit(EMPLOYEE_FORM));
    dispatch(newItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

const isValidCommutingItem = item => item && (item.paymentAmount || item.oneWayFare);

// 住民税の設定値のFormデータをリクエストパラメタ用のフォーマットに変換する
export const reformatEmployeeInhabitantTax = employeeInhabitantTax => {
  let result = { ...employeeInhabitantTax };
  // サーバーは、簡易/月別問わず、月別にすべての値をもっているため、簡易の場合は月ごとの形式に変換する
  if (employeeInhabitantTax.monthSettingType === 'simple') {
    const amounts = [];
    for (let month = 1; month <= 12; month += 1) {
      if (month === employeeInhabitantTax.landmarkMonth) {
        amounts.push({ month, amount: employeeInhabitantTax.taxAmountLandmark });
      } else {
        amounts.push({ month, amount: employeeInhabitantTax.taxAmountAfterLandmark });
      }
    }
    result = { ...employeeInhabitantTax, taxAmounts: amounts };
  }
  // 不要なパラメタの削除
  delete result.taxAmountLandmark;
  delete result.taxAmountAfterLandmark;
  return result;
};

// Edit Employee
export const editEmployee = (data, employeeId, showEmployeeUrl) => async dispatch => {
  try {
    dispatch(startSubmit(EMPLOYEE_FORM));
    dispatch(editItem(REDUCER_NAME));
    const formattedData = { ...data };
    // Reformat employeeCommutings data
    formattedData.employeeCommutings = data.employeeCommutings.filter(
      item => isIncluded(data.employee.meansOfCommunication, item.meansOfCommunication) && isValidCommutingItem(item)
    );

    // Reformat employeeAllowances data
    formattedData.employeeAllowances = data.employeeAllowances.filter(
      item => item.clientPayrollRulesGroupId === data.employeePayrollRule.clientPayrollRulesGroupId
    );

    formattedData.employeeUnitPrices = data.employeeUnitPrices.filter(
      item => item.clientPayrollRulesGroupId === data.employeePayrollRule.clientPayrollRulesGroupId
    );

    formattedData.employeeStandardMonthlies = data.employeeStandardMonthlies.filter(
      item => item.monthlyCompensationHealthInsuranceAmount && item.monthlyRemunerationPensionInsuranceAmount
    );

    // Reformat employeeFamilySupports
    formattedData.employeeFamilySupports = data.familySupport === '1' ? data.employeeFamilySupports : [];

    // Reformat headOfHouseholdName
    if (isHonnin(data.employee.headOfHousehold)) formattedData.employee.headOfHouseholdName = '';

    // Refomat employeeInsurance
    if (formattedData.employeeInsurance.isTargetAgedEmployee && formattedData.employeeInsurance.isAgedEmployee === '') {
      formattedData.employeeInsurance.isAgedEmployee = false;
    }
    formattedData.employeeInhabitantTax = reformatEmployeeInhabitantTax(data.employeeInhabitantTax);

    const image = await getBase64(data.employee.profileImage);

    const httpBody = {
      ...formattedData,
      employeeLeaveOfAbsences: data.employee.employeeLeaveOfAbsences,
      employee: {
        ...data.employee,
        profileImage: image
      },
      employeeAllowances: data.employeeAllowances.map(item => ({
        ...item,
        paymentAmountSecondHalf: item.paymentAmountSecondHalf === null ? '0.0' : item.paymentAmountSecondHalf
      }))
    };
    const editUrl = concatParamsToUrl(Urls.UPDATE_EMPLOYEE_URL, { id: employeeId });

    const response = await axios.put(editUrl, httpBody);

    if (response.data.errors.length > 0) {
      dispatch(editItemFailure(REDUCER_NAME, response.data.errors.messages));
      dispatch(stopSubmit(EMPLOYEE_FORM));
    } else {
      const tutorialStep3 = getTutorialStep3();
      if (tutorialStep3) {
        const { run, employeeId } = tutorialStep3;
        if (run) {
          setTutorialStep3({ run, stepIndex: 0, tourCompleted: true, type: 'checkInfo', employeeId });
        }
      }
      redirectTo(showEmployeeUrl);
    }
  } catch (exception) {
    scrollToTop();
    dispatch(stopSubmit(EMPLOYEE_FORM));
    let errMessages = i18next.t('reducers.employees.failed_to_update_employee_information');
    if (exception.response.data) {
      errMessages = exception.response.data.errors.messages;
    }
    dispatch(editItemFailure(REDUCER_NAME, errMessages));
  }
};

// Delete Employee
export const deleteEmployee = employeeId => async dispatch => {
  try {
    const deleteUrl = concatParamsToUrl(Urls.EMPLOYEE_DELETE, { id: employeeId });
    await axios.delete(deleteUrl);
    dispatch(setGlobalSuccesses(i18next.t('reducers.employees.employee_information_deleted')));
    redirectTo(Urls.EMPLOYEES_LIST);
  } catch (e) {
    dispatch(setGlobalErrors(e.response.data.errors.messages));
  }
};

export const newEmployeeFromAdmin = data => async dispatch => {
  try {
    dispatch(newItem(REDUCER_NAME));
    const httpBody = { employee: data, employeePayrollRule: data };
    const response = await axios.post(Urls.CREATE_EMPLOYEE_FROM_ADMIN, httpBody);

    if (response.data.errors.length > 0) {
      dispatch(editItemFailure(REDUCER_NAME, response.data.errors.messages));
    } else {
      const values = response.data.payload;
      // It redirects immediately after success, so we do not need dispatch here.
      // Uncomment below line in otherwise.
      // dispatch(newSuccessAction(REDUCER_NAME, values.employee));
      const nextUrl = data.afterSubmit === SAVE_THEN_MOVE_TO_EDIT ? concatParamsToUrl(Urls.EMPLOYEE_EDIT, { id: values.employee.id }) : Urls.EMPLOYEES_LIST;

      redirectTo(nextUrl);
    }
  } catch (exception) {
    scrollToTop();
    dispatch(newItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const importEmployeeCsv = (data, operation = 'create') => async dispatch => {
  try {
    dispatch(newItem(REDUCER_NAME));

    const params = paramsWithFile(data, ['employee_csv']);
    params.append('operation', operation);
    await axios.post(Urls.IMPORT_EMPLOYEE_CSV, params);
    dispatch(setGlobalSuccesses(i18next.t('reducers.employees.received_the_file')));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const fetchCityCodeByName = setCityCallback => async (dispatch, getState) => {
  try {
    const city = selector(getEmployeeFormValues(getState()), EMPLOYEE_FORM, 'city');
    if (!city) {
      dispatch(setGlobalErrors('基本情報で市区町村を入力してください。'));
      return;
    }

    const response = await axios.get(`${Urls.SEARCH_CITY_CODE_WITH_NAME_URL}?${serializeHttpGetParams({ query: city })}`);
    setCityCallback(response.data.payload.city);
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};

export const EmployeeImportImage = file => async dispatch => {
  const formData = new FormData();
  let image = '';
  try {
    formData.append('image', file, file.fileName);
    if (file.type.split('/')[0] === 'image') {
      await axios.post(Urls.IMPORT_EMPLOYEE_IMAGE, formData).then(response => {
        image = response;
      });
      dispatch(setGlobalSuccesses(i18next.t('reducers.employees.image_uploaded_successfully')));
      return `http://static.payroll.jobcan.in.th/${image.data.imageUrl}`;
    }
    const errorMessages = [
      i18next.t('reducers.employees.this_cannot_update_any_type_of_file_except_image'),
      i18next.t('reducers.employees.please_upload_the_image')
    ];
    dispatch(setGlobalErrors(errorMessages));
  } catch (error) {
    const errorMessages = [i18next.t('reducers.employees.an_error_occurred'), i18next.t('reducers.employees.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  }
};

export const fetchPayslipByMonth = employeePayId => async dispatch => {
  try {
    dispatch(setLoading(REDUCER_NAME, true));
    const { data } = await axios.get(`/employees/my_data/fetch_payslip?id=${employeePayId}`);
    const { notDisplay0YenAllowances, allPayrolls } = data.payload.values;
    const removedZeroYenPayrolls = allPayrolls.length > 0 ? removeZeroYenItem(allPayrolls, notDisplay0YenAllowances) : allPayrolls;

    dispatch(
      mergeItemFields(REDUCER_NAME, {
        selectingPayroll: removedZeroYenPayrolls.find(payslip => payslip.id === employeePayId) || {}
      })
    );
    dispatch(setLoading(REDUCER_NAME, false));
  } catch (error) {
    dispatch(setGlobalErrors(error.response.data.errors.messages));
    dispatch(fetchItemFailure(REDUCER_NAME, error));
  }
};

export const fetchPaymentMonthByYear = year => async dispatch => {
  try {
    dispatch(setLoading(REDUCER_NAME, true));
    const { data } = await axios.get(`/employees/my_data/fetch_payslip?year=${year}`);
    const { notDisplay0YenAllowances, allPayrolls } = data.payload.values;
    const removedZeroYenPayrolls = allPayrolls.length > 0 ? removeZeroYenItem(allPayrolls, notDisplay0YenAllowances) : allPayrolls;

    dispatch(
      mergeItemFields(REDUCER_NAME, {
        allPayrolls: removedZeroYenPayrolls,
        selectingPayroll: removedZeroYenPayrolls[0] || {}
      })
    );
    dispatch(setLoading(REDUCER_NAME, false));
  } catch (_error) {
    const errorMessages = [i18next.t('reducers.employees.failed_to_retrieve_data'), i18next.t('reducers.employees.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
